// UsernameLayout.jsx
import React from 'react';
import { useLoaderData, Outlet } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchUserByUsername from '../action/fetchUserByUsername.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import prepareUserProfileMeta from '../resource/prepareUserProfileMeta.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getUserDataByUsername from '../selector/getUserDataByUsername.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import {
  getUserPostFeedNameWithQuery,
  fetchFeedTotalCount,
} from '../resource/fetchFeedTotalCount.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const shouldRevalidate = ({ currentParams: curr, nextParams: next }) =>
  curr.listType !== next.listType || curr.publishStatus !== next.publishStatus;

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('UsernameLayout loader()');
  const errorLog = legacySSRError.extend('UsernameLayout loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });
    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Fetch user by username first
    await store.dispatch(
      fetchUserByUsername({
        username: params.username,
        httpProxyHeaders,
      })
    );

    // Get state and userId after user fetch
    const state = store.getState();
    const userId = getUserDataByUsername(state, params.username, 'userId');

    // If userId is not found, user doesn't exist, should return 0 for messageCount
    let messageCount = null;
    if (userId) {
      const feedNameWithQuery = getUserPostFeedNameWithQuery({
        state,
        userId,
        listType: params.listType,
      });
      if (feedNameWithQuery) {
        const result = await fetchFeedTotalCount({
          feedNameWithQuery,
          httpProxyHeaders,
        });
        messageCount = result;
      }
    } else {
      // User not found, set messageCount to 0 to trigger noindex
      messageCount = 0;
    }

    const storeData = state.toJS();
    log('fetched data', { store: storeData, userId });

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const canonicalPath = `/u/${params.username}${params.listType ? `/${params.listType}` : ''}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareUserProfileMeta({
      state,
      userId,
      username: params.username,
      listType: params.listType,
      hostname: request?.headers?.get('host') || 'swag.live',
      includeJsonLd: true,
      canonical,
      alternates,
      messageCount,
      i18n: i18nInstance,
    });
    log('meta data prepared', { meta, userId });

    return {
      data: { store: { users: storeData.users } },
      params,
      meta,
      userId,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const UsernameLayout = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'UsernameLayout',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <Outlet />
    </>
  );
};

export default UsernameLayout;
